<template>
	<v-container class="mx-auto overflow-auto">
		<v-container v-if="isLoading" fluid class="justify-center align-center">
			<v-progress-circular indeterminate size="35" width="3" color="primary" />
			<p class="mt-2">{{ $t('loading') }}</p>
		</v-container>

		<div
			style="min-height: 140px"
			v-if="noAssignedDevices && Array.isArray(surveyForPatientReport) && surveyForPatientReport?.length <= 0"
			class="d-flex align-center justify-center">
			<span style="border: #e3e3e3 dashed 2px; color: #a3a3a3" class="pa-3 text-subtitle-1">
				{{ $t('thisUserHasNoDevices') }}
			</span>
		</div>

		<v-row
			v-else-if="!isLoading"
			ref="scrollRow"
			color="transparent"
			align="center"
			class="d-flex flex-row flex-nowrap elevation-0 pa-2 overflow-x-auto"
			:class="[isOverflowing ? 'justify-start' : 'justify-center', !isOverlay && isOverflowing ? 'pb-0' : '']"
			style="-webkit-overflow-scrolling: touch; gap: 16px"
			:style="{ paddingTop: !isOverlay && isOverflowing ? '6px !important' : '' }">
			<v-card
				v-for="(item, i) in vitals"
				:key="`vitals_${i}`"
				hover
				elevation-1
				@click="toggleDrawer(i)"
				:ripple="false"
				class="border-radius7 my-1"
				height="140"
				width="180"
				min-width="180">
				<v-card-title class="pa-3 pb-0 ma-0">
					<div class="mx-auto">
						<v-img
							class="mx-auto"
							:src="require('@/assets/icons/' + item.deviceIcon + 'black.svg')"
							:alt="item.deviceName"
							max-width="17"
							@click.stop="enterManualMeasurementsDialog(item)"></v-img>
						<div>
							<span class="color-black font-weight-medium font-size14">
								{{ $i18n.locale === 'en' ? item.deviceName : item.deviceNameEsp }}
							</span>
						</div>
					</div>
				</v-card-title>

				<v-card-text class="pa-0 ma-0" style="height: 70px">
					<v-container class="pa-0 ma-0" dense>
						<v-col cols="12" class="pa-0 ma-0 d-flex align-center flex-column" justify="center" align="center">
							<div v-if="item.measurementsData?.length > 0">
								<p class="ma-0 py-0 black--text font-weight-bold">{{ lastMeasurement(item) }}</p>
								<p class="font-size14 text-decoration ma-0">{{ $t('latest-measurement') }}</p>
								<p class="font-size14 text-decoration ma-0">{{ lastMeasuredTime(item) }}</p>
							</div>

							<p v-else class="color-primary font-size14 font-weight-medium">{{ $t('no-recorded-data') }}</p>
						</v-col>
					</v-container>
				</v-card-text>
			</v-card>

			<v-card
				v-for="(survey, index) in surveyForPatientReport"
				:key="index"
				hover
				elevation-1
				@click="openAssessments(index)"
				:ripple="false"
				class="border-radius7 my-1"
				height="140"
				width="180"
				min-width="180">
				<v-card-text class="pa-3 ma-0" style="height: 70px">
					<v-container class="pa-0 ma-0" dense>
						<v-col cols="12" class="pa-0 ma-0 d-flex align-center flex-column" justify="center" align="center">
							<v-icon class="color-black">mdi-format-list-checks</v-icon>
							<span class="color-black font-weight-medium font-size14">{{ survey.templateName }}</span>
							<span class="font-size14 mt-3">{{ getLocalizedRecurrence(survey.recurrenceRuleOfLastRecurrence) }}</span>
						</v-col>
					</v-container>
				</v-card-text>
			</v-card>
		</v-row>
		<add-manual-measurement-dialog :dialog="dialog" :vitals="selectedVital" />
	</v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import AddManualMeasurementDialog from '@/views/dialogs/AddManualMeasurementDialog';
import { formatterFilter } from '@/utils/luxon-formater';
export default {
	components: {
		AddManualMeasurementDialog,
	},
	data () {
		return {
			isLoading: false,
			doubleClickCounter: 0,
			selectedVitalsDrawer: false,
			selectedDevice: [],
			selectedVital: null,
			noAssignedDevices: false,
			vitals: [],
			changeNames: true,
			dialog: false,
			loading: true,
			timeLocalization: this.$i18n.locale,
			isOverflowing: false,
			isOverlay: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			hubs: (state) => state.nosessionhubs.hubs,
			surveyForPatient: (state) => state.mentalhealth.surveyPatentDetails,
			surveyForPatientReport: (state) => state.mentalhealth.surveyReportForPatient,
		}),
		...mapGetters({ userSettings: 'authentication/getUserSettings' }),
		assignedMedicalDevices () {
			return this.hubs?.assignedMedicalDevicesList?.map((item) => ({
				deviceDefaultValuesJson: item.deviceDefaultValuesJson,
				deviceDefaultExtremeValuesJson: item.deviceDefaultExtremeValuesJson,
				deviceType: item.deviceType,
				measurementsData: item.measurementsData,
				deviceName: item.deviceName,
				id: item.id,
				isManualMeasurement: item.isManualMeasurement,
				device: item,
				deviceNameEsp: item.deviceNameEsp,
			}));
		},
	},
	methods: {
		hasOverlayScrollbar () {
			const el = document.createElement('div');
			el.style.cssText = ['width:10px', 'height:10px', 'overflow:scroll', 'position:absolute', 'top:-9999px'].join(';');
			document.body.appendChild(el);

			const thickness = el.offsetWidth - el.clientWidth;
			document.body.removeChild(el);

			// thickness === 0 → overlay (macOS/iOS style); >0 → classic
			return thickness === 0;
		},
		getRecurrenceLocalized (reports) {
			if (this.$i18n.locale === 'es') {
				switch (reports) {
					case 'Daily':
						return 'A diario';
					case 'Every 4th Day':
						return 'Cada 4 días';
					case 'Monthly':
						return 'Mensual';
					case 'Every 2nd Day':
						return 'Cada 2 días';
					case 'Every 3rd Day':
						return 'Cada 3er día';
					case 'Once':
						return 'Una vez';
				}
			}
			return reports;
		},
		getLocalizedRecurrence (recurrenceRule) {
			return this.getRecurrenceLocalized(recurrenceRule);
		},
		enterManualMeasurementsDialog (item) {
			this.doubleClickCounter++;
			if (this.doubleClickCounter === 1) {
				this.timer = setTimeout(() => {
					this.doubleClickCounter = 0;
				}, 300);
				return;
			}
			clearTimeout(this.timer);
			this.doubleClickCounter = 0;
			this.selectedDevice = item;
			this.changeNames = false;
			this.selectedVital = item;
			this.dialog = !this.dialog;
		},
		synchronizeDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', !this.updateDrawer);
		},

		formatDate (submitAtDate) {
			const date = new Date(submitAtDate);
			const options = { month: 'long', day: 'numeric', year: 'numeric' };
			return date.toLocaleDateString('en-US', options);
		},
		openAssessments (index) {
			this.$emit('toggledrawer2', { isOpen: true, selectedSurvey: this.surveyForPatientReport[index] });
		},
		async getSurveys () {
			this.isLoading = true;
			const headers = {
				patientId: this.detailedUser.userData.id,
				filterCase: this.$cookies.get('datafilter'),
				limitForOccurrences: 50,
			};
			await this.$store.dispatch('mentalhealth/getSurveyReportForPatient', headers).then((res) => {
				this.$store.commit('mentalhealth/GET_SURVEY_FLAG', res.resFlag);
				this.isLoading = false;
			});
		},
		toggleDrawer (index) {
			this.selectedVitalsDrawer = this?.vitals[index];
			this.$emit('toggledrawer', this.selectedVitalsDrawer);
		},
		closeDrawer () {
			this.selectedVitalsDrawer = false;
		},
		async getHubsData (data) {
			this.isLoading = true;
			await this.$store.dispatch('nosessionhubs/getHubsData', data).then((this.loading = false));
			this.isLoading = false;
		},
		fixVitalnames () {
			this.vitals = this.assignedMedicalDevices;
			if (this.vitals.length === 0) {
				this.noAssignedDevices = true;
				return;
			}
			// Assigning device icons and names based on deviceType
			for (const device in this?.vitals) {
				switch (this?.vitals[device]?.deviceType) {
					case 3:
						this.vitals[device].deviceIcon = 'blood_pressure_';
						this.vitals[device].deviceName = 'Blood Pressure';
						break;
					case 6:
						this.vitals[device].deviceIcon = 'glucose_meter_';
						this.vitals[device].deviceName = 'Glucose Meter';
						break;
					case 1:
						this.vitals[device].deviceIcon = 'thermometer_';
						this.vitals[device].deviceName = 'Thermometer';
						break;
					case 2:
						this.vitals[device].deviceIcon = 'oxymeter_';
						this.vitals[device].deviceName = 'Oximeter';
						break;
					case 5:
						this.vitals[device].deviceIcon = 'scale_';
						this.vitals[device].deviceName = 'Weight Scale';
						break;
					case 7:
						this.vitals[device].deviceIcon = 'cholesterol_meter_';
						this.vitals[device].deviceName = 'Cholesterol Meter';
						break;

					default:
						break;
				}
			}
		},

		formattedTime (date) {
			return formatterFilter(date, 'datetime_med', this.timeLocalization, null);
		},

		lastMeasurement (device) {
			if (device.measurementsData?.length === 0) {
				return;
			}
			const measurements = JSON.parse(device.measurementsData[0].measuredDataJson);

			switch (device.deviceType) {
				case 1: // Thermometer
					if (this.userSettings.Units.toLowerCase() === 'metric') {
						return measurements?.Temperature_C + ' °C';
					} else if (this.userSettings.Units.toLowerCase() === 'imperial') {
						return measurements.Temperature_F + ' °F';
					}
					break;
				case 3: // Blood Pressure
					return measurements.Dias !== undefined ? measurements.Sys + '/' + measurements.Dias + ' mmHg' : '';
				case 2: // Oximeter
					return measurements?.BPM !== undefined
						? measurements?.Spo2 +
								' SpO2 ' +
								measurements?.BPM +
								' bpm ' +
								' Pi ' +
								(measurements?.PI).toFixed(2) +
								' %'
						: '';
				case 5: // Weight Scale
					return this.userSettings.Units.toLowerCase() === 'metric'
						? measurements.weight_kg + ' kg'
						: measurements.weight_lbs + ' lb';
				case 6: // Glucose Meter
					return this.userSettings.Concentration.toLowerCase() === 'mmol/l'
						? measurements.glucose_mmolL + ' mmol/L'
						: measurements.glucose_mgL + ' mg/dL';
				case 'Cholesterol Meter':
					return this.userSettings.Concentration.toLowerCase() === 'mmol/l'
						? measurements.cholesterol_mmolL + ' mmol/L'
						: measurements.cholesterol_mgL + ' mg/dL';
			}
		},

		lastMeasuredTime (device) {
			const data = device.measurementsData
				.slice() // clone to avoid mutating original
				.sort((a, b) => new Date(b.measuredAtUtc) - new Date(a.measuredAtUtc));

			const last = data[0];
			const result = this.formattedTime(last?.measuredAtUtc);
			return result ? `${result}` : 'No data';
		},
		checkOverflow () {
			const refEl = this.$refs.scrollRow;
			if (!refEl) return;
			const dom = refEl.$el ? refEl.$el : refEl;
			if (!dom) return;
			this.isOverflowing = dom.scrollWidth > dom.clientWidth;
		},
	},
	async mounted () {
		this.isOverlay = this.hasOverlayScrollbar();
		const data = {
			id: this.detailedUser.userData.id,
			filter: this.$cookies.get('datafilter'),
		};
		await this.getHubsData(data);
		this.fixVitalnames();
		await this.getSurveys();
		this.$nextTick(() => {
			this.checkOverflow();
		});
		window.addEventListener('resize', this.checkOverflow);
	},

	beforeDestroy () {
		window.removeEventListener('resize', this.checkOverflow);
	},

	watch: {
		vitals: {
			handler () {
				this.$nextTick(this.checkOverflow);
			},
			deep: true,
		},
		surveyForPatientReport: {
			handler () {
				this.$nextTick(this.checkOverflow);
			},
			deep: true,
		},
	},
};
</script>
